import { useState } from 'react';

import { DEFAULT_TIME_FILTER, TIME_FILTER_OPTIONS } from './dashboardSelectors';
import type { TimeFilterValue } from './dashboardSelectors';

const VALID_TIME_FILTER_VALUES = new Set<string>(TIME_FILTER_OPTIONS.map(option => option.value));

/**
 * Reads/writes a collapsed `boolean` to `localStorage` under `storageKey`.
 * Reads `localStorage.getItem(key) === 'true'`, so anything other than the
 * literal string `'true'` (including a missing key) defaults to `false`
 * (expanded) — matching today's always-expanded behavior for first-time
 * visitors and for anyone whose stored value predates this feature.
 *
 * Uses a lazy `useState` initializer, matching `List.tsx`'s
 * `collapsedSections` precedent, so the first paint never flashes expanded
 * before collapsing.
 *
 * @param storageKey - `localStorage` key, unique per section so the two
 *   sections' collapsed states persist independently.
 */
export function usePersistedCollapsed(storageKey: string): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = (): void => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        // Ignore quota/availability errors — state still updates for this session.
      }
      return next;
    });
  };

  return [collapsed, toggleCollapsed];
}

/**
 * Reads/writes a `TimeFilterValue` to `localStorage` under `storageKey`,
 * validated against `TIME_FILTER_OPTIONS` on every read — a stored value is
 * untrusted input (it could be stale from a prior app version, or edited
 * directly in devtools), so an invalid value falls back to
 * `DEFAULT_TIME_FILTER` rather than being trusted as-is.
 * Pattern: docs/design-patterns/frontend/security.md
 *
 * Stores a plain string (not JSON) — matches `useProjectFiltering.ts`'s
 * `STORAGE_SORT_KEY` convention, not the JSON-array convention used for
 * `Set`-valued filters (there is nothing to serialize but the single value).
 *
 * Uses a lazy `useState` initializer (never `useEffect` + a second render)
 * so the very first paint already reflects the stored value with no flash —
 * matches `List.tsx`'s `collapsedSections` / `useProjectFiltering.ts`'s
 * `readChipsFromStorage` precedent.
 *
 * @param storageKey - `localStorage` key, unique per section (e.g. one key
 *   for the In Progress window, a different key for Recently Completed) so
 *   the two sections' filters persist independently.
 */
export function usePersistedTimeFilter(
  storageKey: string,
): [TimeFilterValue, (value: TimeFilterValue) => void] {
  const [value, setValue] = useState<TimeFilterValue>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null && VALID_TIME_FILTER_VALUES.has(stored)) {
        return stored as TimeFilterValue;
      }
    } catch {
      // Storage disabled/unavailable — fall back to the default below.
    }
    return DEFAULT_TIME_FILTER;
  });

  const setPersistedValue = (next: TimeFilterValue): void => {
    setValue(next);
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      // Ignore quota/availability errors — state still updates for this session.
    }
  };

  return [value, setPersistedValue];
}
